'use client';

import { Box, Card, Container, Typography } from '@mui/material';

import { CoreValues } from '@/services/types';
import { getImageUrl } from '@/services/utils';

import OrangeLine from '../../atoms/OrangeLine';

interface CoreValuesSectionProps {
  data?: CoreValues;
}

export default function CoreValuesSection({ data }: CoreValuesSectionProps) {
  const fallbackData: CoreValues = {
    id: 1,
    title: 'GIÁ TRỊ CỐT LÕI',
    values: [
      {
        id: 1,
        title: 'Giá trị cốt lõi',
        description:
          'Hoạt động minh bạch, hiệu quả, tiếp tục tiệm cận với những tiêu chuẩn cao của quản trị kinh doanh quốc tế. Tạo ra những sản phẩm và dịch vụ với chất xám cao, đáp ứng tối đa nhu cầu của tầng lớp khách hàng.',
        icon: {
          id: 1,
          name: '🏭',
          url: '',
          width: 48,
          height: 48,
        },
      },
    ],
  };

  const coreValues = data || fallbackData;
  console.log(coreValues);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '550px',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50px',
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: coreValues.backgroundImage
            ? `url(${getImageUrl(coreValues.backgroundImage)})`
            : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 1,
          maxWidth: '1920px',
          mx: 'auto',
          height: '450px',
        }}
      />

      <Container
        maxWidth='lg'
        sx={{
          position: 'relative',
          zIndex: 2,
          py: 8,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          mt: 8,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant='h2'
            component='h2'
            sx={{
              fontFamily: 'Radio Canada, sans-serif',
              fontWeight: 700,
              fontSize: '40px',
              lineHeight: 1.2,
              color: '#001A44',
              mb: 3,
              textTransform: 'uppercase',
            }}
          >
            {coreValues.title}
          </Typography>

          <OrangeLine width='321px' height='5px' mx='auto' />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: { xs: '24px', sm: '32px', md: '44px' },
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            justifyContent: 'center',
            maxWidth: '1000px',
            mx: 'auto',
          }}
        >
          {coreValues.values && coreValues.values.length > 0 ? (
            coreValues.values.map(value => (
              <Box key={value.id}>
                <Card
                  sx={{
                    height: '100%',
                    boxShadow: '0px 0px 20px 0px rgba(132, 132, 132, 0.2)',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    padding: {
                      xs: '20px 16px',
                      sm: '25px 20px',
                      md: '30px 25px',
                    },
                    width: { xs: '100%', sm: '280px', md: '303px' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      mb: 4,
                      height: '47.1px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {value.icon && value.icon.url ? (
                      <Box
                        component='img'
                        src={getImageUrl(value.icon)}
                        alt={value.icon.alternativeText || value.icon.name}
                        sx={{
                          width: '53.9px',
                          height: '47.1px',
                          objectFit: 'contain',
                        }}
                        onError={_e => {
                          // Icon failed to load - could add fallback handling here
                        }}
                        onLoad={() => {
                          // Icon loaded successfully
                        }}
                      />
                    ) : (
                      <Typography
                        variant='h1'
                        component='h3'
                        sx={{
                          fontSize: '48px',
                          lineHeight: 1,
                        }}
                      >
                        🏭
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    variant='h3'
                    component='h3'
                    sx={{
                      fontFamily: 'Radio Canada, sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: '24px', sm: '28px', md: '32px' },
                      lineHeight: 1.25,
                      color: '#333333',
                      mb: 2,
                      width: { xs: '100%', sm: '200px', md: '208px' },
                      minHeight: { xs: '24px', sm: '28px', md: '32px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {value.title}
                  </Typography>

                  <Typography
                    variant='body1'
                    sx={{
                      fontFamily: 'SF Pro, sans-serif',
                      fontSize: { xs: '14px', sm: '15px', md: '16px' },
                      lineHeight: 1.5,
                      color: '#4D4C4C',
                      textAlign: 'center',
                      width: { xs: '100%', sm: '200px', md: '208px' },
                      minHeight: { xs: '14px', sm: '15px', md: '16px' },
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {value.description}
                  </Typography>
                </Card>
              </Box>
            ))
          ) : (
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
              <Typography variant='body1' color='text.secondary'>
                No core values available
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
